import { Box, Flex } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
// Internal ATS grid — keeps analytical tables consistent with the rest of the app.
import { Table } from '@/components/ats';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { KText } from '@/ui-library/components/text';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { parseMatchPct } from '../../../helpers';
import { useBlockFollowup } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import { SuggestedActionChips } from '../SuggestedActionChips';
import { CandidateHoverProfile } from '../candidate/CandidateHoverProfile';
import { MatchPill } from '../primitives';
import type { DataTableBlockProps, DataTableRow } from './DataTableBlock.types';

const CANDIDATE_PREFIX = '/candidates/';

// A candidate name in the grid opens the same profile-preview panel as the
// result cards (click-to-open, not navigation); the panel's own icon deep-links
// to the full profile and floats the chat over it. The card self-fetches the
// rest from the id, so a bare id + name summary is enough here.
const CandidateNameCell = ({ id, name }: { id: string; name: string }) => (
  <CandidateHoverProfile candidate={{ id, name, headline: '', tags: [] }}>
    {(bind) => (
      <Box
        onClick={bind.onClick}
        className="block cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap text-primary-900 text-sm font-medium hover:underline"
      >
        {name}
      </Box>
    )}
  </CandidateHoverProfile>
);

// Rank columns ("#" / "Rank") duplicate row order, so they're dropped.
const isRankColumn = (header: string): boolean => {
  const h = header.trim().toLowerCase();
  return h === '#' || h === 'rank' || h === 'no.' || h === 'no';
};

const looksLikeEntityID = (id: string | undefined): boolean =>
  !!id && /^\d+$/.test(id);

// Backend `entity` picks the deep-link route; contact profiles are the default.
const entityHref = (
  entity: 'contact' | 'job' | undefined,
  id: string
): string => (entity === 'job' ? `/jobs/${id}` : `/candidates/${id}`);

// Analytical tables (rankings/comparisons/summaries/lists) from present_table.
export const DataTableBlock = ({ data }: DataTableBlockProps) => {
  const {
    intro,
    columns,
    rows,
    ids,
    entity,
    footnote,
    variant,
    suggestedActions,
  } = data;
  const openFloatingBridge = useAgentForgeStore((s) => s.openFloatingBridge);

  const { sendFollowup, isSending } = useBlockFollowup({
    allIds: ids ?? [],
    selectedIds: [],
  });

  const renderedCols: { header: string; origIdx: number }[] = [];
  columns.forEach((c, i) => {
    if (!isRankColumn(c)) renderedCols.push({ header: c, origIdx: i });
  });

  const tableColumns = renderedCols.map((c, cIdx) => ({
    Header: () => (
      <Text variant="label" as="span" truncate>
        {c.header}
      </Text>
    ),
    accessor: `col${cIdx}`,
    style: { overflow: 'hidden' },
    Cell: (cell: { value?: string; row?: { original?: DataTableRow } }) => {
      const value = cell?.value ?? '';
      const href = cell?.row?.original?._href;
      if (cIdx === 0 && href) {
        // Candidate names open the profile-preview panel (like the result
        // cards); job names keep the deep-link + float on the name itself.
        if (href.startsWith(CANDIDATE_PREFIX)) {
          return (
            <CandidateNameCell
              id={href.slice(CANDIDATE_PREFIX.length)}
              name={value}
            />
          );
        }
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={href}
                  onClick={() => openFloatingBridge(href)}
                  className="block overflow-hidden text-ellipsis whitespace-nowrap text-primary-900 text-sm font-medium hover:underline"
                >
                  {value}
                </Link>
              </TooltipTrigger>
              <TooltipContent style={{ zIndex: 2100 }}>{value}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      const isSkillCol = c.header.trim().toLowerCase().includes('skill');
      if (isSkillCol && value) {
        const skills = value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        return (
          <ViewMorePopover
            list={skills}
            type="project"
            renderInitial={(list) =>
              list.map((skill, idx) => (
                <TruncatedTextWithTooltip
                  key={idx}
                  text={skill}
                  maxW="150px"
                  size="sm-medium"
                  toolTipVariant="default"
                  textTransform="capitalize"
                  borderRadius="6px"
                  p="0.125em 0.5em"
                  bgColor="secondary-50"
                />
              ))
            }
            renderOthers={() => (
              <Flex flexDir="column">
                {skills.map((skill, idx) => (
                  <Flex key={idx} alignItems="center" gap=".25em">
                    <KText fontSize="sm" color="secondary-700">
                      {skill}
                    </KText>
                  </Flex>
                ))}
              </Flex>
            )}
          />
        );
      }
      const isMatchCol = c.header.trim().toLowerCase() === 'match';
      const pct = isMatchCol ? parseMatchPct(value) : null;
      if (isMatchCol && pct !== null) {
        return <MatchPill pct={pct} label={value} />;
      }
      // Generic cells: truncation is a last resort, not the norm — the table
      // itself sizes columns to content (tableLayout auto + horizontal
      // scroll). When a value still overflows the column cap, the full text
      // is one hover away.
      return (
        <TruncatedTextWithTooltip
          text={value || '-'}
          display="block"
          maxW="320px"
          fontSize="sm"
          color="secondary-900"
        />
      );
    },
  }));

  const tableData: DataTableRow[] = rows.map((row, rIdx) => {
    const id = ids?.[rIdx];
    const obj: DataTableRow = {};
    renderedCols.forEach((c, cIdx) => {
      obj[`col${cIdx}`] = row[c.origIdx] ?? '';
    });
    obj._href = looksLikeEntityID(id)
      ? entityHref(entity, id as string)
      : undefined;
    return obj;
  });

  const variantLabel: Record<NonNullable<typeof variant>, string> = {
    ranking: 'Ranking',
    comparison: 'Comparison',
    list: 'List',
    summary: 'Summary',
  };
  const badge = variant && variant !== 'list' ? variantLabel[variant] : null;

  return (
    <Box data-test-id="agentforge-data-table-block">
      {/* Intro + badge above, footnote + actions below — only the grid is boxed. */}
      {(intro || badge) && (
        <Flex align="center" justify="space-between" gap="10px" mb="8px">
          {intro ? (
            <Text variant="label" as="span" className="text-secondary-900">
              {intro}
            </Text>
          ) : (
            <Box />
          )}
          {badge && (
            <Box
              px="8px"
              py="2px"
              borderRadius="full"
              bg="primary-50"
              color="primary-700"
              fontSize="12px"
              fontWeight="600"
              letterSpacing="0.02em"
              textTransform="uppercase"
              flexShrink={0}
            >
              {badge}
            </Box>
          )}
        </Flex>
      )}

      {/* @ts-expect-error ATS Table is JS with loose prop types. */}
      <Table
        columns={tableColumns}
        data={tableData}
        tableId="agentforge-data-table"
        skipPagination
        layoutStyles={{ border: 'none' }}
        // auto layout sizes columns to their content instead of splitting the
        // width equally — equal splits forced every cell to ellipsize at
        // ~25% width ("Rails - primary backgrou…"). Wide tables scroll
        // horizontally inside TableLayout's overflow=auto wrapper.
        tableStyles={{ width: '100%', tableLayout: 'auto' }}
      />

      {footnote && (
        <Box mt="8px">
          <Text variant="p" as="span" className="text-secondary-600">
            {footnote}
          </Text>
        </Box>
      )}

      {suggestedActions && suggestedActions.length > 0 && (
        <Box mt="10px">
          <SuggestedActionChips
            actions={suggestedActions}
            onSelect={sendFollowup}
            disabled={isSending}
            testIdPrefix="agentforge-data-table-suggested"
          />
        </Box>
      )}
    </Box>
  );
};
